import { PreviewPageItem } from '@/courses/interfaces/preview-page-item.interface';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Emit, Inject } from 'vue-property-decorator';

import { EdPreviewFilePagesProvider } from '../ed-preview-file-pages.provider';

@Component({name: 'ed-preview-file-page'})
export default class EdPreviewFilePage extends Vue {
  @Inject()
  PreviewFilePagesProvider: EdPreviewFilePagesProvider;

  @Prop() readonly item: PreviewPageItem; 
  @Prop() readonly selectable: boolean; 
  @Prop() readonly selectedPagesIds: any[]; 
  @Prop() readonly type: string; 
  @Prop() readonly indexPage: number; 
  @Prop({default: false}) readonly canRotate: boolean;
  @Prop() readonly rotateDegreesInfo: any; 

  public canvasHeight: number = 180;
  public degrees: number = 0;
  public canvasWidth: number = null;
  public rendering = false;
  public canvas : HTMLCanvasElement =  null;
  public timeIntervalSubscription = null;
  public pageImage = '';

  @Emit('onPageChanged')
  emitOnPageChanged() {
    return {page:this.item.page._pageIndex, rotateDegrees: this.degrees};
  }

  get selected(): boolean {
    if(this.selectedPagesIds) {
      return this.selectedPagesIds.includes(this.item.page._pageIndex);
    } else {
      return false;
    }
  }

  get idPage() {
    return this.item.page._pageIndex + this.type;
  }

  get containerStyles() {
    const sizes = this.containerSizes;
    const width = sizes.width ? `${sizes.width}px` : '';
    const height = sizes.height ? `${sizes.height + 26}px` : '';
    
    return { width, height }
  }

  get containerSizes() {
    const width = this.canvasWidth || '';

    return { width, height: this.canvasHeight || 0 }
  }

  createCanvasPage(previewPageItem: PreviewPageItem): Promise<string> {
    return new Promise((resolve, reject) => {
      const { page, scale } = previewPageItem;
      const viewport: any = page.getViewport({ scale });
      this.canvas = document.createElement('canvas');
      const pageImagen : HTMLImageElement = document.createElement('img');
      pageImagen.id = this.idPage + 'img';
      pageImagen.onload = () => {
        this.$emit('imageLoaded', this.item.page._pageIndex)
      };

      this.canvas.width = previewPageItem.width;
      this.canvas.height = previewPageItem.height;
      pageImagen.width  = previewPageItem.width;
      pageImagen.height = previewPageItem.height;
      
      pageImagen.setAttribute('loading', 'lazy');
      pageImagen.classList.add('bsx-p-2');
      const canvasContext = this.canvas.getContext('2d');
      const renderContext = { canvasContext, viewport };

      page.render(renderContext).promise.then(() => {
        const imageData = this.canvas.toDataURL('image/jpeg', 0.2);
        pageImagen.src = imageData;
        this.pageImage = imageData;
        this.releaseCanvas(this.canvas);
        resolve(imageData);
      });
    });
  }

  getRotateDegrees(degrees): number {
    this.degrees = degrees + 90;
    if(this.degrees == 360) {
      this.degrees = 0;
    }
    return this.degrees;
  }

  rotate() {
    let cont: any = document.getElementById(this.idPage);
    let img: any = cont.querySelector('img');
    const degrees = this.getRotateDegrees(this.degrees);
    img.style = `transform: rotate(${degrees}deg);`;
    if(degrees == 90 || degrees == 270) {
      
      const resize = this.PreviewFilePagesProvider.rightScale({
        cont: { 
          clientWidth: cont.clientWidth,
          clientHeight: cont.clientHeight
        },
        img: {
          clientWidth: img.clientWidth,
          clientHeight: img.clientHeight
        }
      })
      img.style = `transform: rotate(${degrees}deg) scale(${resize});`;
    }
    this.emitOnPageChanged();
  }

  setCanvasSize(width: number, height: number) {
    this.canvasWidth = width;
    this.canvasHeight = height + 80;
  }

  releaseCanvas(canvas : HTMLCanvasElement) {
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    ctx && ctx.clearRect(0, 0, 1, 1);
    canvas.remove();
  }

  async selectPage() {
    if (this.selectable) {
      const item = { ...this.item };
      
      if (!item.rendered) {
        this.rendering = true;
        const image = await this.createCanvasPage(item);

        item.image = image;
        item.rendered = true;
        
        this.rendering = false;
      }

      this.$emit('selectPage', item);
    }
  }

  async startRender(emitResult = true) {
    if (!this.item.rendered) {
      const image = await this.createCanvasPage(this.item);
      if (emitResult) {
        this.PreviewFilePagesProvider.setPageRendered(this.item.id, image);
      }

      return image;
    }
    
    return null;
  }

  isPageRendered() {
    const isRendered = this.PreviewFilePagesProvider.isPageRendered(this.item.id);

    if (isRendered) {
      this.pageImage = this.PreviewFilePagesProvider.getPageImage(this.item.id);

      this.PreviewFilePagesProvider.removeFromQueueToRender(this.item.id);

      clearInterval(this.timeIntervalSubscription);
    }

    return isRendered;
  }

  verifyStartRender() {
    this.PreviewFilePagesProvider.addToQueueToRender(this.item.id);

    this.timeIntervalSubscription = setInterval(() => {
      const isPageRendered = this.isPageRendered();
      const isPagePendingToRender = this.PreviewFilePagesProvider.isPageInQueueToRender(this.item.id);

      if (!isPageRendered) {
        if (!isPagePendingToRender) {
          this.PreviewFilePagesProvider.addToQueueToRender(this.item.id);
  
          return;
        }
  
        if (this.PreviewFilePagesProvider.hasToRenderNow(this.item.id)) {
          this.startRender();
  
          clearInterval(this.timeIntervalSubscription);
        }
      }
    }, 500);
  }

  setPage() {
    this.canvasWidth = this.item.width;

    if (!this.PreviewFilePagesProvider.isPageRendered(this.item.id)) {
      this.verifyStartRender();

      return;
    } else {
      this.pageImage = this.PreviewFilePagesProvider.getPageImage(this.item.id);
    }

    if (this.rotateDegreesInfo) {
      const div: any = document.getElementById(this.idPage + 'img');

      div.style = `transform: rotate(${this.rotateDegreesInfo.rotateDegrees}deg);`;
    }
  }

  mounted() {
    this.setPage();
  }

  beforeDestroy() {
    if (this.canvas) {
      this.releaseCanvas(this.canvas);
    }
  
    if (this.timeIntervalSubscription) {
      clearInterval(this.timeIntervalSubscription);

      this.timeIntervalSubscription = null;
    }
    
    this.PreviewFilePagesProvider.removeFromQueueToRender(this.item.id);
    this.rendering = false;
  }
}